import { styled } from '@material-ui/core';
import React from 'react';
import { ThemeRes } from 'Web/Themes/ThemeRes';
import { LaizeBandDimensions } from '../../v3/LaizeBands';
import { MapConstants } from 'Plugins/FloorPlan/MapConstants';
import { LaizeBandItemV3 } from '../../v3/LaizeBandTrace';
import { BandType, LaizeBand } from '../../Laize';
import { SvgAttributesWith } from 'Plugins/FloorPlan/Widgets/SvgTypes';
import { IRoom, StairsStepType } from 'Plugins/FloorPlan/Models/IRoom';
import { I18n } from 'Locales/I18nService';

export const BandsColorPalette = {
    BandStroke: '#000000',
    BandColor: 'rgba(40,185,72, 0.7)',
    BandColorHighlight: '#28B948',

    BandNew: 'rgba(40,185,72, 0.7)',
    BandNewHighlight: '#28B948',
    BandReused: 'rgba(90, 210, 244, 0.7)',
    BandReusedHighlight: '#5AD2F4',
    BandReusable: 'rgba(237, 49, 83, 0.3)',
    BandReusableHighlight: 'rgba(237, 49, 83, 1)',
};

export const LegendConstants = {
    MARGIN: 24,
    LEGEND_WIDTH: 450,
    LEGEND_STAIRS_WIDTH: 600,
    LEGEND_TEXT_START_Y: 40,
};

export const BandText = styled('text')({
    fontFamily: ThemeRes.Fonts.fontFamilyNunitoSans,
    fontWeight: 900,
    fontSize: 18,
});
export const TextSpan = styled('tspan')({
    fontWeight: 400,
    fontSize: 14,
    fontFamily: ThemeRes.Fonts.fontFamilyNunitoSans,
    textTransform: 'none',
    stroke: 'none',
});

export const BandChip = ({ children, fill, ...attrs }: React.PropsWithChildren<SvgAttributesWith<unknown>>) => {
    return (
        <g {...attrs} style={{ pointerEvents: 'none' }}>
            <circle r={18} fill={fill} />
            <BandText
                x={0}
                y={7}
                textAnchor="middle"
                style={{
                    fill: '#FFFFFF',
                    fontSize: 18,
                    fontFamily: ThemeRes.Fonts.fontFamilyJost,
                    textTransform: 'uppercase',
                    fontWeight: 400,
                }}>
                {children}
            </BandText>
        </g>
    );
};
export type LaizeBandProps = {
    band: LaizeBandItemV3;
    rooms: IRoom[];
    index: number;
    bandDetails: Array<LaizeBand>;
    bands: LaizeBandItemV3[];
    exportMode?: boolean;
};

//#region LEGEND

export const LegendMapTitle = ({ children, fill, ...attrs }: React.PropsWithChildren<SvgAttributesWith<unknown>>) => {
    return (
        <text
            role="LegendMapTitle"
            style={{
                fontSize: 24,
                fontWeight: 400,
                fill: '#1C1A37',
                fontFamily: ThemeRes.Fonts.fontFamilyJost,
                textTransform: 'uppercase',
            }}
            textAnchor="left"
            {...attrs}>
            {children}
        </text>
    );
};

const LegendTitle = styled('text')({
    fontFamily: ThemeRes.Fonts.fontFamilyJost,
    fontWeight: 400,
    fontSize: 18,
    textTransform: 'uppercase',
    fill: '#000000',
});
const LegendSubtitle = ({ children, ...attrs }: React.PropsWithChildren<SvgAttributesWith<{ focused?: boolean }>>) => {
    return children ? <TextSpan {...attrs}>{children}</TextSpan> : null;
};

export type LegendItemProps = {
    posY: number;

    title: string;
    subtitle: string;
    text1?: string;
    text2?: string;
    text3?: string;
};
export const LegendItem = (props: SvgAttributesWith<LegendItemProps>) => {
    const { posY, subtitle, title, text1, text2, text3, fill, ...attrs } = props;
    return (
        <g {...attrs}>
            <BandChip transform={`translate(20, ${posY}) scale(1)`} fill={fill}>
                {title}
            </BandChip>
            <LegendTitle x={66} y={posY + 3}>
                <LegendSubtitle
                    x={48}
                    dy={3}
                    style={{ lineHeight: '24px', fontWeight: 800, textTransform: 'uppercase' }}>
                    {subtitle}
                </LegendSubtitle>
                <LegendSubtitle x={48} dy={30}>
                    {text1}
                </LegendSubtitle>
                <LegendSubtitle x={48} dy={27}>
                    {text2}
                </LegendSubtitle>
                <LegendSubtitle x={48} dy={27}>
                    {text3}
                </LegendSubtitle>
            </LegendTitle>
        </g>
    );
};

export const getTextDimensions = (dimensions: LaizeBandDimensions) => {
    const meter = MapConstants.meter;
    const displayedWidth = Math.round((dimensions.width * 100) / meter);
    const displayedHeight = Math.round((dimensions.height * 100) / meter);
    const textDimensions = `Largeur ${displayedWidth}cm x Longueur ${displayedHeight}cm`;
    return textDimensions;
};

export const legendYOffset = (dimensions: string[]) => {
    const [textDimensions, surfaceCoveredDimensions, leftOverDimensions] = dimensions;
    const spacing = 27;
    const textHeight = 19;
    const titleYoffset = 3 + textHeight;
    const text1Yoffset = !textDimensions ? 0 : 30 + textHeight;
    const text2Yoffset = !surfaceCoveredDimensions ? 0 : spacing + textHeight;
    const text3Yoffset = !leftOverDimensions ? 0 : textHeight;
    return titleYoffset + text1Yoffset + text2Yoffset + text3Yoffset;
};

export type LegendPositionProps = { posY: number; focused?: boolean };

export const getLegendFocusStyle = (bandType: BandType, focused?: boolean, highlighted?: string) => {
    if (!focused) {
        return { fill: '#191A2F' };
    }
    switch (bandType) {
        case BandType.New:
            return { fill: highlighted ?? BandsColorPalette.BandNewHighlight };
        case BandType.Reused:
            return { fill: highlighted ?? BandsColorPalette.BandReusedHighlight };
        case BandType.Reusable:
            return { fill: highlighted ?? BandsColorPalette.BandReusableHighlight };
        default:
            return {};
    }
};

//#endregion

export const indexToLetter = (index: number) => String.fromCharCode(65 + index);

export const StairTypeShort = (type?: StairsStepType) =>
    type ? I18n.get(`LaizeCalcDialog_StairType_Short_${type}`) : '??';
export const StairType = (type?: StairsStepType) => (type ? I18n.get(`LaizeCalcDialog_StairType_${type}`) : '??');

export const StairShortFullString = (staircaseIndex: number, stairIndex: number, stairType?: StairsStepType) =>
    `Esc${staircaseIndex + 1}-${StairTypeShort(stairType)}${stairIndex + 1}`;

export const StairFullString = (staircaseIndex: number, stairIndex: number, stairType?: StairsStepType) =>
    `ESC${staircaseIndex + 1}-${StairType(stairType)}-${stairIndex + 1}`;
